import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import { DateRangePresets, DateScale, isDateDimension } from '@/shared/types';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import * as ROIBreakdownAPI from '../api/roiBreakdownAPI';
import type { DataConfig, Meta, ROIBreakdownDataResponse } from './types';

interface State {
  meta:
    | {
        data: Meta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  config: DataConfig | null;
}

const initialState: State = {
  meta: {
    status: DataStatus.idle,
    data: null,
  },
  config: null,
};

export const roiBreakdownSlice = createAppSlice({
  name: 'roiBreakdown',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return ROIBreakdownAPI.getMeta();
      },
      {
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;
            const { status } = state.roiBreakdown.meta;

            if (isLoading(status) || isFinished(status)) {
              return false;
            }
          },
        },
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
      },
    ),
    updateCohortDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.cohortDateScale = action.payload;
    }),
    updateCohortDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.cohortDateRange = action.payload;
      },
    ),
    updateRevenueDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.revenueDateScale = action.payload;
    }),
    updateRevenueDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.revenueDateRange = action.payload;
      },
    ),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.probabilisticAttribution = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.filters = action.payload;
    }),
    updateDimension: create.reducer<string>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.dimension = action.payload;
    }),
    updateMetric: create.reducer<string>((state, action) => {
      if (!state.config) {
        return;
      }
      state.config.metric = action.payload;
    }),
    updateShowRelativeValues: create.reducer<boolean>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.showRelativeValues = action.payload;
    }),
    toggleExcludedSplitOptions: create.reducer<{
      options: string[];
      checked: boolean;
    }>((state, action) => {
      if (!state.config) {
        return;
      }

      const excludedOptionsSet = new Set(state.config.excludedSplitOptions);

      if (!action.payload.checked) {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.add(option);
        });
      } else {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.delete(option);
        });
      }

      state.config.excludedSplitOptions = Array.from(excludedOptionsSet);
    }),
    updateConfig: create.reducer<DataConfig>((state, action) => {
      state.config = action.payload;
    }),
    resetConfig: create.reducer((state) => {
      if (!state.meta.data) {
        return;
      }

      state.config = getDefaultConfig(state.meta.data);
    }),
    setupDefaultExcludedSplitOptions: create.reducer<string[]>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        const DEFAULT_INCLUDED_OPTIONS_COUNT = 10;
        const optionsToExclude = action.payload.slice(
          DEFAULT_INCLUDED_OPTIONS_COUNT,
        );

        state.config.excludedSplitOptions = optionsToExclude;
      },
    ),
    toggleAllExcludedSplitOptions: create.reducer<{
      checked: boolean;
      splitOptions: string[];
    }>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.excludedSplitOptions = action.payload.checked
        ? []
        : action.payload.splitOptions;
    }),
  }),
  selectors: {
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoading: (state) => isLoading(state.meta.status),
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
    selectMeta: (state) => state.meta.data,
    selectConfig: (state) => {
      return state.config;
    },
  },
});

const getDefaultConfig = (meta: Meta): DataConfig => {
  return {
    cohortDateScale: DateScale.DAY,
    cohortDateRange: { preset: DateRangePresets.ALL_TIME },
    revenueDateScale: DateScale.DAY,
    revenueDateRange: { preset: DateRangePresets.LAST_90_DAYS },
    probabilisticAttribution: true,
    metric: meta.defaultAxis.metric,
    dimension: meta.defaultAxis.dimension,
    excludedSplitOptions: [],
    showRelativeValues: false,
    filters: [],
  };
};

function isMetaFinished(meta: State['meta']): meta is {
  data: Meta;
  status: DataStatus.finished;
} {
  return isFinished(meta.status);
}

export const getData =
  (chart: DataConfig): AppThunk<Promise<ROIBreakdownDataResponse>> =>
  (_dispatch, getState) => {
    const state = getState();
    const { meta } = state.roiBreakdown;

    if (!isMetaFinished(meta)) {
      return Promise.reject(new Error('Meta not initialized'));
    }

    const { dimensionsFilters, metricsFilters } = prepareFilters(
      chart.filters,
      meta.data.metrics,
      meta.data.dimensions,
    );

    return ROIBreakdownAPI.getData({
      split: chart.dimension,
      metrics: [chart.metric],
      cohortDateScale: chart.cohortDateScale,
      cohortDateRange: prepareDateRangeValueObject(chart.cohortDateRange),
      revenueDateScale: chart.revenueDateScale,
      revenueDateRange: prepareDateRangeValueObject(chart.revenueDateRange),
      probabilisticAttribution: chart.probabilisticAttribution,
      dimensionsFilters,
      metricsFilters,
    }).then((data) => {
      return {
        ...data,
        // options should be sorted in decreasing order for better readability (use case: cohort date as split by option)
        splitOptions: Object.fromEntries(
          Object.entries(data.splitOptions).map(([key, value]) => {
            const sortingOrderSign = isDateDimension(meta.data.config, key)
              ? -1
              : 1;

            return [
              key,
              value.sort((a, b) => {
                return sortingOrderSign * a.localeCompare(b);
              }),
            ];
          }),
        ),
      };
    });
  };

export const {
  initMeta,
  resetConfig,
  updateConfig,
  updateCohortDateScale,
  updateCohortDateRange,
  updateRevenueDateScale,
  updateRevenueDateRange,
  updateProbabilisticAttribution,
  toggleExcludedSplitOptions,
  updateFilters,
  updateDimension,
  updateMetric,
  updateShowRelativeValues,
  setupDefaultExcludedSplitOptions,
  toggleAllExcludedSplitOptions,
} = roiBreakdownSlice.actions;
export const {
  selectMetaDataStatus,
  selectMetaIsLoading,
  selectMetaIsLoaded,
  selectMeta,
  selectConfig,
} = roiBreakdownSlice.selectors;
